import { FC, useEffect, cloneElement } from "react";
import { Col, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";

import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import FormField from "components/molecules/FormField/FormField";
import ZipcodeLookupSpinner from "components/atoms/ZipcodeLookupSpinner/ZipcodeLookupSpinner";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import Radio from "components/atoms/Radio/Radio";
import { AddressV2FormValues } from "api/models";
import { useGetLocodes } from "api/queries/common/useGetLocodes";
import { useGetTimezones } from "api/queries/common/useGetTimezones";
import usePreferredContact from "hooks/usePreferredContact";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import usePermissions from "hooks/usePermissions";
import useAddressV2Fields from "hooks/useAddressV2Fields";

type AddressV2FormProps = {
  /**
   * Initial preferred contact from selected address
   */
  preferredContact: {
    id: number;
    name: string;
    email: string;
    phone_number: string;
  } | null;
};

const AddressV2Form: FC<AddressV2FormProps> = function (props) {
  const { preferredContact } = props;

  const intl = useIntl();
  const { watch, control, setValue, getValues } = useFormContext<AddressV2FormValues>();

  const { permissions } = usePermissions();
  const { address_availability } = JSON.parse(permissions ?? "{}").company_shipper_settings;

  const { handleContactSearch, handleContactRenderOption, contactSelectOptions, selectedContactDetails } =
    usePreferredContact({ preferredContact, watch });

  const [isLocationReferenceChecked, isCustomAddressReferenceChecked, isAddressWithinAirportOrPort] = useWatch({
    name: ["location_reference_number_enabled", "custom_address_reference_enabled", "address_within_airport_or_port"],
    control,
  });

  const {
    fields,
    countryId,
    countryCode,
    stateId,
    statesOptions,
    isStateMandatory,
    onSelectCountry: hookOnSelectCountry,
    onZipcodeBlur,
    showZipcodeLookupIndicator,
  } = useAddressV2Fields({
    control,
    watch,
    setValue,
    enable_new_address_structure: true,
    allowCustomCity: true,
    enableZipcodeLookup: true,
  });

  const { data: locodes = [], isInitialLoading: isLocodesFetching } = useGetLocodes(countryId!, {
    select: (data: any) => data.map(({ name, un_locode, id }: any) => ({ label: `${name} (${un_locode})`, value: id })),
    enabled: !!countryId,
  });

  const stateName =
    statesOptions.find((s: any) => s.value?.toString() === stateId?.toString())?.label ||
    (stateId ? getValues("state_name") : undefined);
  const { data: timezones = [], isInitialLoading: isTimezonesFetching }: any = useGetTimezones(
    { country_code: countryCode, ...(stateName && { state_name: stateName }) },
    { enabled: !!countryCode, select: (data: any) => data.map((tz: any) => ({ value: tz.value, label: tz.name })) },
  );

  useEffect(() => {
    if (timezones.length === 1) setValue("timezone", timezones[0].value);
    const currentTimezone = getValues("timezone");
    if (currentTimezone && !timezones.find((tz: any) => tz.value === currentTimezone)) {
      setValue("timezone", null);
    }
  }, [timezones]);

  const locodeOptions =
    locodes.length === 0 && watch("locode_id")
      ? [{ value: watch("locode_id"), label: String(watch("locode_id")) }]
      : locodes;

  const onSelectCountry = (event: any) => {
    hookOnSelectCountry(event);
    setValue("locode_id", null);
  };

  const sortedLocations = useSortedPaymentLocations();

  const eventContext = "Edit Address Dialog";

  return (
    <>
      {address_availability === "Payment Location" && (
        <Row className="m-0 mt-3">
          <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
            <h6 className="required">
              <FormattedMessage id="general.paymentLocation" defaultMessage="Payment Location" />
            </h6>
          </Col>
          <Col sm={8} className="pr-md-0">
            <div className="input-element ">
              <SearchSelect
                required
                placeholder={intl.formatMessage({
                  id: "general.select",
                  defaultMessage: "Select",
                })}
                name="selected_shipper_location"
                options={sortedLocations}
                preselect={false}
              />
            </div>
          </Col>
        </Row>
      )}

      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6 className="required">
            <FormattedMessage id="general.name" defaultMessage="Name" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">
            <FormField type="textinput" eventContext={eventContext} id="name" mandatory field_id="name" />
          </div>
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6>
            <FormattedMessage id="settings.address-book.locationType" defaultMessage="Location Type" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <Radio
            className="border-0 pt-0 pb-0 justify-content-between flex-nowrap"
            labelClassName="font-b3 gap-sm"
            customColClass="pl-0 pr-0 flex-initial width-fit"
            checkmarkClassName="mr-0"
            name="address_label"
            customOption
            options={[
              {
                value: "none",
                label: "None",
              },
              {
                value: "company_location",
                label: "Company location",
              },
              {
                value: "warehouse",
                label: "Warehouse",
              },
            ]}
            defaultChecked="none"
          />
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6 className="required">
            <FormattedMessage id="general.country" defaultMessage="Country" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">{cloneElement(fields[0].children, { onSelect: onSelectCountry })}</div>
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6>
            <FormattedMessage
              id="new-request.schedule.addressBookDialog.zipCodeOrPostalCode"
              defaultMessage="Zip / Postal Code"
            />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <FormField
                  type="textinput"
                  eventContext={eventContext}
                  id="zipcode"
                  field_id="zipcode"
                  onBlur={(event) => {
                    onZipcodeBlur(undefined, event.currentTarget.value);
                  }}
                />
              </div>
              <ZipcodeLookupSpinner visible={showZipcodeLookupIndicator} />
            </div>
          </div>
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6 className={isStateMandatory ? "required" : ""}>{fields[1].label}</h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">{fields[1].children}</div>
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6 className="required">
            <FormattedMessage id="general.city" defaultMessage="City" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">
            {cloneElement(fields[2].children, {
              enableManualValue: true,
              allowNew: true,
              newSelectionPrefix: "",
              manualName: "custom_city_name",
              preselect: false,
            })}
          </div>
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6 className="required">
            <FormattedMessage id="general.address" defaultMessage="Address" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">
            <FormField
              type="textinput"
              eventContext={eventContext}
              id="street_address"
              mandatory
              field_id="street_address"
            />
          </div>
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6>
            <FormattedMessage id="new-request.schedule.addressBookDialog.address2" defaultMessage="Address Line 2" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">
            <FormField
              type="textinput"
              eventContext={eventContext}
              id="street_address_line_two"
              field_id="street_address_line_two"
            />
          </div>
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6 className="required">
            <FormattedMessage id="settings.address-book.timezone" defaultMessage="Timezone" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">
            <SearchSelect
              preselect={false}
              required
              placeholder={intl.formatMessage({
                id: "general.select",
                defaultMessage: "Select",
              })}
              name="timezone"
              options={timezones}
              disable={!watch("country_id") || isTimezonesFetching}
              isLoading={isTimezonesFetching}
            />
          </div>
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6>
            <FormattedMessage id="settings.address-book.eori" defaultMessage="EORI code" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">
            <FormField type="textinput" eventContext={eventContext} id="eori" field_id="eori" />
          </div>
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 mb-10 mb-md-0" sm={4}>
          <h6>
            <FormattedMessage id="settings.address-book.airportPort" defaultMessage="Airport / Port" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <Checkbox
            checkboxLabel="Address is within an airport or port"
            name="address_within_airport_or_port"
            required={false}
            providerSections
          />
          {isAddressWithinAirportOrPort && (
            <SearchSelect
              placeholder={intl.formatMessage({
                id: "general.Search",
                defaultMessage: "Search",
              })}
              classes="mt-2"
              label="UN/LOCODE"
              name="locode_id"
              isLoading={isLocodesFetching}
              disable={isLocodesFetching}
              options={locodeOptions}
            />
          )}
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 mb-10 mb-md-0" sm={4}>
          <h6>
            <FormattedMessage id="settings.address-book.additionalIdentifiers" defaultMessage="Additional identifiers" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">
            <Checkbox
              checkboxLabel="Location reference number"
              name="location_reference_number_enabled"
              required={false}
              providerSections
            />
            {isLocationReferenceChecked && (
              <FormField
                type="textinput"
                eventContext={eventContext}
                id="location_reference_number"
                field_id="location_reference_number"
                containerClasses="mt-2"
              />
            )}
          </div>
          <div className="input-element mt-3">
            <Checkbox
              checkboxLabel="Custom address reference"
              name="custom_address_reference_enabled"
              required={false}
              providerSections
            />
            {isCustomAddressReferenceChecked && (
              <FormField
                type="textinput"
                eventContext={eventContext}
                id="custom_address_reference"
                field_id="custom_address_reference"
                containerClasses="mt-2"
              />
            )}
          </div>
        </Col>
      </Row>
      <Row className="m-0 mt-3">
        <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
          <h6>
            <FormattedMessage id="settings.address-book.preferredContact" defaultMessage="Default Contact Person" />
          </h6>
        </Col>
        <Col sm={8} className="pr-md-0">
          <div className="input-element ">
            <SearchSelect
              placeholder={intl.formatMessage({
                id: "general.select",
                defaultMessage: "Select",
              })}
              name="preferred_contact"
              options={contactSelectOptions}
              preselect={false}
              onInputChange={handleContactSearch}
              handleRenderOption={handleContactRenderOption}
            />
          </div>
        </Col>
      </Row>
      {selectedContactDetails && (
        <Row className="m-0 mt-2">
          <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4} />
          <Col sm={8} className="pr-md-0">
            <div className="input-element">
              <div className="d-flex flex-column">
                {selectedContactDetails.email && (
                  <div className="d-flex mb-1">
                    <span className="font-weight-bold" style={{ minWidth: "120px" }}>
                      Email:
                    </span>
                    <span className="ml-2">{selectedContactDetails.email}</span>
                  </div>
                )}
                {selectedContactDetails.phone && (
                  <div className="d-flex">
                    <span className="font-weight-bold" style={{ minWidth: "120px" }}>
                      Contact Number:
                    </span>
                    <span className="ml-2">{selectedContactDetails.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default AddressV2Form;
